import Link from 'next/link';
import { FOCUS_RING } from '@/components/ui/focusRing';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * A link in the floating header: the pill whose ink wells up from below.
 *
 * After React Bits' PillNav (MIT + Commons Clause, used inside the product as
 * its licence allows), rebuilt without its animation runtime: on hover a disc
 * of ink — the primary action's own fill, `--rt-button` — rises through the
 * pill from its bottom edge, and the label rolls up and out while an ink-on-
 * ink copy rolls in behind it. Pure CSS on the house spring, so it costs no
 * JavaScript and renders on the server with the rest of the header.
 *
 * The rise is a dome, not a wipe: the disc is wider than the pill, so what
 * crosses the pill is the flat top of a large circle, which reads as liquid
 * filling rather than a panel sliding.
 *
 * `PillButton` is the same object for a header entry that opens something
 * rather than going somewhere (Pricing opens the plans).
 */

const PILL =
  'group/pill relative isolate inline-flex h-10 shrink-0 items-center overflow-hidden rounded-full px-4 '
  + `text-body font-medium text-[var(--rt-ink-dim)] outline-none ${FOCUS_RING} `;

const ROLL =
  'transition-transform duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)] '
  + 'group-hover/pill:-translate-y-full group-focus-visible/pill:-translate-y-full motion-reduce:transition-none';

function PillInner({ children }: { children: ReactNode }) {
  return (
    <>
      <span
        aria-hidden
        className={
          'absolute left-1/2 top-full -z-10 aspect-square w-[240%] -translate-x-1/2 rounded-full bg-[var(--rt-button)] '
          + 'transition-transform duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)] '
          + 'group-hover/pill:-translate-y-[3.5rem] group-focus-visible/pill:-translate-y-[3.5rem] motion-reduce:transition-none'
        }
      />
      <span className="relative block h-10 overflow-hidden">
        <span className={`flex h-10 items-center ${ROLL}`}>{children}</span>
        <span aria-hidden className={`absolute inset-x-0 top-full flex h-10 items-center text-[var(--rt-button-label)] ${ROLL}`}>
          {children}
        </span>
      </span>
    </>
  );
}

export default function PillLink({
  href, children, className = '',
}: {
  href: string;
  children: string;
  className?: string;
}) {
  return (
    <Link href={href} className={PILL + className}>
      <PillInner>{children}</PillInner>
    </Link>
  );
}

export function PillButton({
  children, className = '', ...rest
}: { children: string } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  return (
    <button type="button" {...rest} className={PILL + className}>
      <PillInner>{children}</PillInner>
    </button>
  );
}
